import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Sparkles, Plus, Trash2, ExternalLink, RefreshCw, CheckCircle, AlertTriangle,
  XCircle, TrendingUp, TrendingDown, Eye, MousePointerClick, Search, BarChart3, Loader2,
  Calendar, Tag, Globe, Lightbulb, FileText, Target, ChevronRight, Home, Users,
  Zap, ArrowRight, BookOpen, Clock, ToggleLeft, ToggleRight, MapPin, Award, Flame
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, Cell
} from 'recharts';

const API = '/api';
const CITY_COLORS = ['#1d4ed8','#059669','#d97706','#7c3aed','#db2777','#dc2626','#0891b2','#65a30d'];

const PRIORITY_CONFIG = {
  high: { label: 'High', cls: 'bg-red-50 text-red-700 border-red-200' },
  medium: { label: 'Medium', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  low: { label: 'Low', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
};

const STATUS_ICON = { pass: CheckCircle, warn: AlertTriangle, fail: XCircle };
const STATUS_CLS = {
  pass: 'text-emerald-600 bg-emerald-50',
  warn: 'text-amber-600 bg-amber-50',
  fail: 'text-red-600 bg-red-50',
};

// ── Sub-components ────────────────────────────────────────────────────────────

function StatCard({ icon: Icon, label, value, color, delta }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-3 ${color}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="text-2xl font-bold text-slate-900">{value?.toLocaleString('en-IN') ?? '—'}</div>
      <div className="text-xs text-slate-500 mt-0.5">{label}</div>
      {delta !== null && delta !== undefined && (
        <div className={`flex items-center gap-1 mt-1.5 text-xs font-semibold ${delta > 0 ? 'text-emerald-600' : delta < 0 ? 'text-red-500' : 'text-slate-400'}`}>
          {delta > 0 ? <TrendingUp className="w-3 h-3" /> : delta < 0 ? <TrendingDown className="w-3 h-3" /> : null}
          <span>{delta > 0 ? `+${delta}%` : delta < 0 ? `${delta}%` : '—'}</span>
          <span className="text-slate-400 font-normal">vs prev</span>
        </div>
      )}
    </div>
  );
}

// ── Auto-Blog Opportunities ───────────────────────────────────────────────────

function AutoBlogOpportunities({ onUseTopic }) {
  const [loading, setLoading] = useState(false);
  const [opps, setOpps] = useState(null);

  const scan = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/growth/auto-blog/opportunities`, { credentials: 'same-origin' });
      if (!res.ok) throw new Error('Scan failed');
      const d = await res.json();
      setOpps(d.opportunities || []);
    } catch (e) { toast.error(e.message || 'Scan failed'); }
    setLoading(false);
  };

  return (
    <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-5">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-amber-100 rounded-lg flex items-center justify-center">
            <Lightbulb className="w-3.5 h-3.5 text-amber-700" />
          </div>
          <div>
            <h4 className="font-bold text-slate-900 text-sm" style={{fontFamily:'Outfit,sans-serif'}}>Auto-Blog Opportunities</h4>
            <p className="text-xs text-slate-500">Property clusters with many listings but no blog yet</p>
          </div>
        </div>
        <button onClick={scan} disabled={loading} data-testid="scan-opportunities-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs font-bold disabled:opacity-60 transition-colors">
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
          {loading ? 'Scanning…' : 'Scan Now'}
        </button>
      </div>

      {opps === null && (
        <p className="text-xs text-slate-400 mt-3">Click <strong>Scan Now</strong> to detect property clusters that need a blog post.</p>
      )}

      {opps !== null && opps.length === 0 && (
        <p className="text-xs text-slate-500 text-center py-4">All major property clusters already have a blog.</p>
      )}

      {opps !== null && opps.length > 0 && (
        <div className="space-y-2 mt-3">
          {opps.map((opp, i) => (
            <div key={i} className="bg-white rounded-xl border border-amber-100 px-4 py-3 flex items-center gap-3 shadow-sm">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-900">{opp.label}</p>
                <p className="text-xs text-slate-500">{opp.count} live listings · no blog published yet</p>
              </div>
              <button onClick={() => onUseTopic(opp.suggested_topic, opp.suggested_keyword)}
                data-testid={`use-opportunity-${i}`}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-700 hover:bg-purple-800 text-white rounded-lg text-xs font-bold transition-colors whitespace-nowrap">
                <Sparkles className="w-3 h-3" /> Generate Blog
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ── Keyword Gap Alerts ────────────────────────────────────────────────────────

function KeywordGaps({ onOpenWriter }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [suggesting, setSuggesting] = useState({});
  const [suggestions, setSuggestions] = useState({});
  const [bulkSuggesting, setBulkSuggesting] = useState(false);

  const fetchGaps = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/growth/keyword-gaps`, { credentials: 'same-origin' });
      if (!res.ok) throw new Error('Failed to fetch');
      setData(await res.json());
    } catch (e) { toast.error(e.message || 'Failed to load keyword gaps'); }
    setLoading(false);
  };

  const suggestBlog = async (q) => {
    setSuggesting(p => ({ ...p, [q.query]: true }));
    try {
      const res = await fetch(`${API}/growth/keyword-gaps/suggest-blog`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ keyword: q.query, position: q.position, impressions: q.impressions }),
      });
      if (!res.ok) throw new Error('Suggestion failed');
      const sug = await res.json();
      setSuggestions(p => ({ ...p, [q.query]: sug }));
    } catch (e) { toast.error(e.message || 'AI suggestion failed'); }
    setSuggesting(p => ({ ...p, [q.query]: false }));
  };

  const suggestAll = async () => {
    const allQueries = [...(data?.quick_wins || []), ...(data?.growth || [])];
    if (allQueries.length === 0) return;
    setBulkSuggesting(true);
    try {
      const res = await fetch(`${API}/growth/keyword-gaps/bulk-suggest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ queries: allQueries.slice(0, 8) }),
      });
      if (!res.ok) throw new Error('Bulk suggest failed');
      const d = await res.json();
      const newSugs = {};
      for (const s of d.suggestions) {
        if (s.keyword) newSugs[s.keyword] = s;
      }
      setSuggestions(p => ({ ...p, ...newSugs }));
      toast.success(`Generated ${d.suggestions.length} blog ideas!`);
    } catch (e) { toast.error(e.message || 'Bulk suggest failed'); }
    setBulkSuggesting(false);
  };

  const GapRow = ({ q, tier }) => {
    const sug = suggestions[q.query];
    const isSuggesting = suggesting[q.query];
    const tierColor = tier === 'quick' ? 'text-emerald-700 bg-emerald-50' : 'text-blue-700 bg-blue-50';
    return (
      <div className="px-4 py-3 border-b border-slate-50 last:border-0">
        <div className="flex items-start gap-3">
          <span className={`mt-0.5 text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${tierColor}`}>
            #{q.position}
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-800 truncate">{q.query}</p>
            <p className="text-xs text-slate-500">{q.impressions} impressions · {q.clicks} clicks · {q.ctr}% CTR</p>
            {sug && (
              <div className="mt-2 bg-purple-50 border border-purple-100 rounded-xl p-3">
                <p className="text-xs font-bold text-purple-800 mb-0.5">AI Blog Suggestion</p>
                <p className="text-xs text-slate-700 font-semibold">{sug.title}</p>
                <p className="text-xs text-slate-500 mt-0.5">{sug.topic}</p>
                <button onClick={() => onOpenWriter(sug.topic || sug.title, sug.keyword)}
                  data-testid={`write-blog-${q.query.replace(/\s/g,'-')}`}
                  className="mt-2 flex items-center gap-1 px-3 py-1.5 bg-purple-700 hover:bg-purple-800 text-white rounded-lg text-xs font-bold transition-colors">
                  <BookOpen className="w-3 h-3" /> Write This Blog
                </button>
              </div>
            )}
          </div>
          {!sug && (
            <button onClick={() => suggestBlog(q)} disabled={isSuggesting}
              data-testid={`suggest-blog-btn`}
              className="flex items-center gap-1 px-2.5 py-1.5 border border-purple-200 text-purple-700 hover:bg-purple-50 rounded-lg text-xs font-semibold disabled:opacity-60 transition-colors whitespace-nowrap">
              {isSuggesting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
              {isSuggesting ? '…' : 'Suggest Blog'}
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-emerald-100 rounded-lg flex items-center justify-center">
              <Zap className="w-3.5 h-3.5 text-emerald-700" />
            </div>
            <div>
              <h4 className="font-bold text-slate-900 text-sm" style={{fontFamily:'Outfit,sans-serif'}}>Keyword Gap Alerts</h4>
              <p className="text-xs text-slate-500">GSC queries ranking 6–50 — write a blog to reach page 1</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {data?.configured && (data?.quick_wins?.length > 0 || data?.growth?.length > 0) && (
              <button onClick={suggestAll} disabled={bulkSuggesting} data-testid="suggest-all-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-700 hover:bg-purple-800 text-white rounded-lg text-xs font-bold disabled:opacity-60 transition-colors">
                {bulkSuggesting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                {bulkSuggesting ? 'Generating…' : 'Suggest All'}
              </button>
            )}
            <button onClick={fetchGaps} disabled={loading} data-testid="fetch-keyword-gaps-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold disabled:opacity-60 transition-colors">
              {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              {loading ? 'Loading…' : 'Load Gaps'}
            </button>
          </div>
        </div>

        {!data && <p className="text-xs text-slate-400 mt-3">Click <strong>Load Gaps</strong> to pull GSC data. Then use <strong>Suggest All</strong> for instant AI blog ideas.</p>}

        {data && !data.configured && (
          <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-700">
            GSC not configured: {data.reason || 'Check Google credentials in .env'}
          </div>
        )}

        {data?.error && (
          <div className="mt-3 bg-red-50 border border-red-200 rounded-xl p-3 text-xs text-red-700">
            {data.error}
          </div>
        )}
      </div>

      {data?.configured && !data?.error && (
        <>
          {/* Quick Wins */}
          {data.quick_wins?.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 bg-emerald-50">
                <div className="flex items-center gap-2">
                  <Zap className="w-3.5 h-3.5 text-emerald-700" />
                  <h5 className="text-sm font-bold text-emerald-800" style={{fontFamily:'Outfit,sans-serif'}}>
                    Quick Wins — Positions 6–15
                  </h5>
                  <span className="ml-auto text-xs text-emerald-600 font-semibold">{data.quick_wins.length} queries</span>
                </div>
                <p className="text-xs text-emerald-600 mt-0.5">One good blog post can push these to page 1</p>
              </div>
              {data.quick_wins.map((q, i) => <GapRow key={i} q={q} tier="quick" />)}
            </div>
          )}

          {/* Growth */}
          {data.growth?.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 bg-blue-50">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-3.5 h-3.5 text-blue-700" />
                  <h5 className="text-sm font-bold text-blue-800" style={{fontFamily:'Outfit,sans-serif'}}>
                    Growth Opportunities — Positions 16–50
                  </h5>
                  <span className="ml-auto text-xs text-blue-600 font-semibold">{data.growth.length} queries</span>
                </div>
                <p className="text-xs text-blue-600 mt-0.5">Higher effort, but bigger traffic gains long-term</p>
              </div>
              {data.growth.map((q, i) => <GapRow key={i} q={q} tier="growth" />)}
            </div>
          )}

          {data.quick_wins?.length === 0 && data.growth?.length === 0 && (
            <div className="bg-slate-50 rounded-2xl border border-slate-100 p-6 text-center">
              <p className="text-sm text-slate-500">No keyword gaps found in the last 28 days.</p>
              <p className="text-xs text-slate-400 mt-1">This usually means GSC data is still accumulating — check back in a few days.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}


// ── AI Blog Writer ────────────────────────────────────────────────────────────

function AiBlogWriter({ blogs, onRefreshBlogs, prefill, onClearPrefill }) {
  const [topic, setTopic] = useState('');
  const [keyword, setKeyword] = useState('');
  const [tone, setTone] = useState('informative and helpful');
  const [generating, setGenerating] = useState(false);
  const [content, setContent] = useState('');
  const [parsedTitle, setParsedTitle] = useState('');
  const [parsedMeta, setParsedMeta] = useState('');
  const [saving, setSaving] = useState(false);
  const contentRef = useRef('');
  const writerRef = useRef(null);

  // Pre-fill from Auto-Blog Opportunities or Keyword Gaps (cross-tab)
  useEffect(() => {
    if (prefill?.topic) {
      setTopic(prefill.topic);
      setKeyword(prefill.keyword || '');
      setContent(''); setParsedTitle(''); setParsedMeta('');
      onClearPrefill?.();
      writerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      toast.success('Topic pre-filled — click Generate to write the blog!');
    }
  }, [prefill]);

  const generate = async () => {
    if (!topic.trim()) { toast.error('Enter a topic first'); return; }
    setGenerating(true);
    setContent('');
    contentRef.current = '';
    setParsedTitle('');
    setParsedMeta('');
    try {
      const res = await fetch(`${API}/ai/generate-blog`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ topic, target_keyword: keyword || topic, tone }),
      });
      if (!res.ok) throw new Error('Generation failed');
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        contentRef.current += decoder.decode(value, { stream: true });
        setContent(contentRef.current);
      }
      // Parse TITLE and META from response
      const lines = contentRef.current.split('\n');
      const titleLine = lines.find(l => l.startsWith('TITLE:'));
      const metaLine = lines.find(l => l.startsWith('META:'));
      if (titleLine) setParsedTitle(titleLine.replace('TITLE:', '').trim());
      if (metaLine) setParsedMeta(metaLine.replace('META:', '').trim());
      toast.success('Blog post generated!');
    } catch (e) {
      toast.error(e.message || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const saveDraft = async () => savePost('draft');
  const publishNow = async () => savePost('published');

  const savePost = async (status) => {
    if (!content) { toast.error('Generate content first'); return; }
    setSaving(true);
    const articleContent = content
      .split('\n')
      .filter(l => !l.startsWith('TITLE:') && !l.startsWith('META:'))
      .join('\n').replace(/^[\n\-]+/, '').trim();

    const blogTitle = parsedTitle || topic;
    const excerpt = parsedMeta || articleContent.slice(0, 160);

    try {
      const res = await fetch(`${API}/admin/blogs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          title: blogTitle, excerpt, content: articleContent,
          category: 'guide',
          tags: keyword ? keyword.split(',').map(s => s.trim()) : [],
          status,
          published_at: status === 'published' ? new Date().toISOString() : undefined,
        }),
      });
      if (!res.ok) throw new Error('Save failed');
      const savedBlog = await res.json();
      if (status === 'published') {
        toast.success(`Published! Live at /blog/${savedBlog.slug}`);
      } else {
        toast.success('Saved as draft — edit in the Blog tab before publishing.');
      }
      onRefreshBlogs?.();
      setContent(''); setParsedTitle(''); setParsedMeta(''); setTopic(''); setKeyword('');
    } catch {
      toast.error('Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  // Strip header lines for the clean preview
  const articleBody = content
    ? content.split('\n').filter(l => !l.startsWith('TITLE:') && !l.startsWith('META:') && l !== '---').join('\n').trim()
    : '';

  return (
    <div className="space-y-5">
      <div ref={writerRef} className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
            <p className="text-xs text-slate-500">GPT-4.1 writes SEO-optimised posts ready to publish</p>
          </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Blog Topic <span className="text-red-500">*</span></label>
            <input value={topic} onChange={e => setTopic(e.target.value)}
              placeholder="e.g. How to find a good 2 BHK in Mumbai under 20k"
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-100"
              data-testid="blog-topic-input" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Target Keyword</label>
            <input value={keyword} onChange={e => setKeyword(e.target.value)}
              placeholder="e.g. 2 bhk flat for rent in mumbai"
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-100"
              data-testid="blog-keyword-input" />
          </div>
        </div>
        <div className="mb-4">
          <label className="text-xs font-medium text-slate-600 mb-1 block">Writing Tone</label>
          <div className="flex flex-wrap gap-2">
            {['informative and helpful', 'friendly and conversational', 'authoritative and expert'].map(t => (
              <button key={t} onClick={() => setTone(t)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${tone === t ? 'bg-purple-700 text-white border-purple-700' : 'border-slate-200 text-slate-600 hover:border-purple-300'}`}>
                {t.split(' ')[0].charAt(0).toUpperCase() + t.split(' ')[0].slice(1)}
              </button>
            ))}
          </div>
        </div>
        <button onClick={generate} disabled={generating || !topic.trim()}
          className="flex items-center gap-2 px-5 py-2.5 bg-purple-700 hover:bg-purple-800 text-white rounded-xl text-sm font-bold disabled:opacity-60 transition-colors"
          data-testid="generate-blog-btn">
          {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {generating ? 'Writing…' : 'Generate Blog Post'}
        </button>
      </div>

      {(content || generating) && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500">Generated Post</p>
              {parsedTitle && <p className="font-bold text-slate-900 text-sm mt-0.5">{parsedTitle}</p>}
            </div>
            {content && !generating && (
              <div className="flex items-center gap-2">
                <button onClick={saveDraft} disabled={saving}
                  className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 text-slate-600 hover:bg-slate-50 rounded-xl text-xs font-semibold disabled:opacity-60 transition-colors"
                  data-testid="save-draft-btn">
                  {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
                  Save as Draft
                </button>
                <button onClick={publishNow} disabled={saving}
                  className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold disabled:opacity-60 transition-colors"
                  data-testid="publish-now-btn">
                  {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Globe className="w-3 h-3" />}
                  Publish Now
                </button>
              </div>
            )}
          </div>
          {parsedMeta && (
            <div className="mx-5 mt-3 px-3 py-2 bg-blue-50 rounded-lg border border-blue-100">
              <span className="text-xs font-semibold text-blue-700">Meta: </span>
              <span className="text-xs text-blue-800">{parsedMeta}</span>
            </div>
          )}
          <div className="p-5">
            {generating && !articleBody && (
              <div className="flex items-center gap-2 text-slate-400 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" /> Writing your blog post…
              </div>
            )}
            <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans leading-relaxed">
              {articleBody}
              {generating && <span className="inline-block w-1.5 h-4 bg-purple-500 animate-pulse ml-0.5 align-text-bottom" />}
            </pre>
          </div>
        </div>
      )}
      <AutoBlogOpportunities onUseTopic={(t, k) => { setTopic(t); setKeyword(k); setContent(''); setParsedTitle(''); setParsedMeta(''); writerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }} />
    </div>
  );
}

// ── Keyword Planner ───────────────────────────────────────────────────────────

function KeywordPlanner({ onOpenWriter }) {
  const [keywords, setKeywords] = useState([]);
  const [form, setForm] = useState({ keyword: '', target_url: '', priority: 'medium', notes: '' });
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [seedInput, setSeedInput] = useState('');

  const fetchKeywords = useCallback(async () => {
    const res = await fetch(`${API}/growth/keywords`, { credentials: 'same-origin' });
    const d = await res.json();
    setKeywords(d.keywords || []);
  }, []);

  useEffect(() => { fetchKeywords(); }, [fetchKeywords]);

  const add = async () => {
    if (!form.keyword.trim()) { toast.error('Enter a keyword'); return; }
    try {
      const res = await fetch(`${API}/growth/keywords`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin', body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      toast.success('Keyword added');
      setForm({ keyword: '', target_url: '', priority: 'medium', notes: '' });
      fetchKeywords();
    } catch { toast.error('Failed to add keyword'); }
  };

  const remove = async (id) => {
    await fetch(`${API}/growth/keywords/${id}`, { method: 'DELETE', credentials: 'same-origin' });
    setKeywords(prev => prev.filter(k => k.keyword_id !== id));
    toast.success('Removed');
  };

  const aiSuggest = async () => {
    if (!seedInput.trim() && keywords.length === 0) { toast.error('Enter a seed topic'); return; }
    setSuggesting(true);
    setSuggestions([]);
    try {
      const res = await fetch(`${API}/growth/keywords/suggest`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ seed: seedInput || 'rental property India', existing_keywords: keywords.map(k => k.keyword) }),
      });
      const d = await res.json();
      setSuggestions(d.suggestions || []);
    } catch { toast.error('AI suggestion failed'); }
    setSuggesting(false);
  };

  const addSuggestion = async (kw) => {
    const res = await fetch(`${API}/growth/keywords`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ keyword: kw, priority: 'medium' }),
    });
    if (res.ok) {
      setSuggestions(prev => prev.filter(s => s !== kw));
      fetchKeywords();
      toast.success('Added');
    }
  };

  return (
    <div className="space-y-5">
      {/* Add keyword */}
      <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
        <h3 className="font-bold text-slate-900 text-sm mb-3 flex items-center gap-2" style={{fontFamily:'Outfit,sans-serif'}}>
          <Tag className="w-4 h-4 text-blue-600" /> Add Target Keyword
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
          <input value={form.keyword} onChange={e => setForm(f => ({...f, keyword: e.target.value}))}
            placeholder="e.g. 2 bhk flat for rent in mumbai"
            className="sm:col-span-2 px-3 py-2.5 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100"
            data-testid="keyword-input" />
          <select value={form.priority} onChange={e => setForm(f => ({...f, priority: e.target.value}))}
            className="px-3 py-2.5 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-500 bg-white">
            <option value="high">High Priority</option>
            <option value="medium">Medium Priority</option>
            <option value="low">Low Priority</option>
          </select>
        </div>
        <div className="flex gap-3">
          <input value={form.target_url} onChange={e => setForm(f => ({...f, target_url: e.target.value}))}
            placeholder="Target page URL (e.g. /search?city=Mumbai)"
            className="flex-1 px-3 py-2.5 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-500" />
          <button onClick={add}
            className="flex items-center gap-1.5 px-4 py-2.5 bg-blue-700 hover:bg-blue-800 text-white rounded-xl text-sm font-bold transition-colors"
            data-testid="add-keyword-btn">
            <Plus className="w-4 h-4" /> Add
          </button>
        </div>
      </div>

      {/* AI Suggestions */}
      <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
        <h3 className="font-bold text-slate-900 text-sm mb-3 flex items-center gap-2" style={{fontFamily:'Outfit,sans-serif'}}>
          <Lightbulb className="w-4 h-4 text-amber-500" /> AI Keyword Suggestions
        </h3>
        <div className="flex gap-3 mb-3">
          <input value={seedInput} onChange={e => setSeedInput(e.target.value)}
            placeholder="Seed topic (e.g. PG in Bangalore)"
            className="flex-1 px-3 py-2.5 border border-slate-200 rounded-xl text-sm outline-none focus:border-amber-400" />
          <button onClick={aiSuggest} disabled={suggesting}
            className="flex items-center gap-1.5 px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-sm font-bold disabled:opacity-60 transition-colors"
            data-testid="ai-suggest-btn">
            {suggesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {suggesting ? 'Thinking…' : 'Suggest'}
          </button>
        </div>
        {suggestions.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {suggestions.map(s => (
              <button key={s} onClick={() => addSuggestion(s)}
                className="flex items-center gap-1 px-3 py-1.5 bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-800 rounded-lg text-xs font-medium transition-colors group">
                <Plus className="w-3 h-3 group-hover:scale-110 transition-transform" /> {s}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Keyword list */}
      {keywords.length > 0 ? (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
            <h3 className="font-bold text-slate-900 text-sm" style={{fontFamily:'Outfit,sans-serif'}}>
              Your Keywords <span className="text-slate-400 font-normal">({keywords.length})</span>
            </h3>
          </div>
          <div className="divide-y divide-slate-50">
            {keywords.map(k => {
              const pc = PRIORITY_CONFIG[k.priority] || PRIORITY_CONFIG.medium;
              return (
                <div key={k.keyword_id} className="px-5 py-3 flex items-center gap-3 hover:bg-slate-50 transition-colors">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{k.keyword}</p>
                    {k.target_url && (
                      <p className="text-xs text-slate-400 truncate mt-0.5">{k.target_url}</p>
                    )}
                  </div>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${pc.cls}`}>{pc.label}</span>
                  <button onClick={() => remove(k.keyword_id)}
                    className="p-1.5 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <p className="text-sm text-slate-400 text-center py-6">No keywords yet — add your first target keyword above.</p>
      )}

      {/* Keyword Gap Alerts from GSC */}
      <KeywordGaps onOpenWriter={onOpenWriter} />
    </div>
  );
}

// ── SEO Checker ───────────────────────────────────────────────────────────────

function SeoChecker() {
  const [url, setUrl] = useState('');
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);

  const check = async () => {
    if (!url.trim()) { toast.error('Enter a URL'); return; }
    setChecking(true);
    setResult(null);
    try {
      const res = await fetch(`${API}/growth/seo-check`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin', body: JSON.stringify({ url }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Check failed'); }
      setResult(await res.json());
    } catch (e) { toast.error(e.message || 'SEO check failed'); }
    setChecking(false);
  };

  const scoreColor = result
    ? result.score >= 75 ? 'text-emerald-600' : result.score >= 50 ? 'text-amber-600' : 'text-red-600'
    : '';

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
        <h3 className="font-bold text-slate-900 text-sm mb-3 flex items-center gap-2" style={{fontFamily:'Outfit,sans-serif'}}>
          <Globe className="w-4 h-4 text-emerald-600" /> SEO Page Checker
        </h3>
        <p className="text-xs text-slate-500 mb-4">Enter any page URL from your site to get an instant SEO audit.</p>
        <div className="flex gap-3">
          <input value={url} onChange={e => setUrl(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && check()}
            placeholder="https://rentmitrahub.com/blog/your-post-slug"
            className="flex-1 px-3 py-2.5 border border-slate-200 rounded-xl text-sm outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-100"
            data-testid="seo-url-input" />
          <button onClick={check} disabled={checking}
            className="flex items-center gap-1.5 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-bold disabled:opacity-60 transition-colors"
            data-testid="seo-check-btn">
            {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            {checking ? 'Checking…' : 'Check'}
          </button>
        </div>
      </div>

      {result && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500 truncate max-w-xs">{result.url}</p>
              <p className="text-sm font-semibold text-slate-700 mt-0.5">{result.passed}/{result.total} checks passed</p>
            </div>
            <div className={`text-3xl font-black ${scoreColor}`} data-testid="seo-score">
              {result.score}<span className="text-base font-semibold text-slate-400">%</span>
            </div>
          </div>
          <div className="divide-y divide-slate-50">
            {result.checks.map((c, i) => {
              const Icon = STATUS_ICON[c.status] || CheckCircle;
              return (
                <div key={i} className="px-5 py-3 flex items-start gap-3">
                  <div className={`mt-0.5 w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 ${STATUS_CLS[c.status]}`}>
                    <Icon className="w-3.5 h-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800">{c.check}</p>
                    <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{c.message}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Content Calendar ──────────────────────────────────────────────────────────

function ContentCalendar({ blogs }) {
  const grouped = {};
  [...blogs].forEach(b => {
    const date = (b.published_at || b.created_at || '').slice(0, 7);
    if (!grouped[date]) grouped[date] = [];
    grouped[date].push(b);
  });
  const months = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

  const STATUS_MAP = {
    published: { label: 'Published', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    draft: { label: 'Draft', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  };

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2" style={{fontFamily:'Outfit,sans-serif'}}>
            <Calendar className="w-4 h-4 text-blue-600" /> Content Calendar
          </h3>
          <span className="text-xs text-slate-400">{blogs.length} total posts</span>
        </div>
        <p className="text-xs text-slate-500">Your blog publishing history and drafts by month.</p>
      </div>

      {months.length === 0 && (
        <p className="text-sm text-slate-400 text-center py-8">No blog posts yet. Use the AI Blog Writer to create your first post.</p>
      )}

      {months.map(month => (
        <div key={month} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="px-5 py-3 bg-slate-50 border-b border-slate-100">
            <p className="text-xs font-bold text-slate-600 uppercase tracking-wider">
              {new Date(month + '-01').toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
              <span className="ml-2 text-slate-400 font-normal normal-case">{grouped[month].length} post{grouped[month].length !== 1 ? 's' : ''}</span>
            </p>
          </div>
          <div className="divide-y divide-slate-50">
            {grouped[month].map(b => {
              const sc = STATUS_MAP[b.status] || STATUS_MAP.draft;
              return (
                <div key={b.blog_id} className="px-5 py-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{b.title}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{b.category} · {b.views || 0} views</p>
                  </div>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${sc.cls}`}>{sc.label}</span>
                  {b.slug && (
                    <a href={`/blog/${b.slug}`} target="_blank" rel="noreferrer"
                      className="p-1.5 rounded-lg text-slate-300 hover:text-blue-600 hover:bg-blue-50 transition-colors">
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Analytics ─────────────────────────────────────────────────────────────────

const DATE_OPTS = [
  { label: '7d', days: 7 },
  { label: '28d', days: 28 },
  { label: '90d', days: 90 },
];

function calcDelta(current, previous) {
  if (!previous || previous === 0) return null;
  return Math.round((current - previous) / previous * 100);
}

function GrowthAnalytics() {
  const [selectedDays, setSelectedDays] = useState(28);
  const [data, setData] = useState(null);
  const [googleData, setGoogleData] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async (days) => {
    setLoading(true);
    try {
      const [internal, google] = await Promise.all([
        fetch(`${API}/growth/analytics?days=${days}`, { credentials: 'same-origin' }).then(r => r.json()),
        fetch(`${API}/growth/google-analytics?days=${days}`, { credentials: 'same-origin' }).then(r => r.json()),
      ]);
      setData(internal);
      setGoogleData(google);
    } catch { /* keep old data on error */ }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(28); }, [loadData]);

  const handleDaysChange = (d) => {
    setSelectedDays(d);
    loadData(d);
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (!data) return <p className="text-sm text-slate-400 text-center py-8">Could not load analytics</p>;

  return (
    <div className="space-y-5">
      {/* Date range selector */}
      <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-sm font-bold text-slate-900" style={{fontFamily:'Outfit,sans-serif'}}>Analytics Overview</p>
          <p className="text-xs text-slate-500">Comparing to previous {selectedDays}-day period</p>
        </div>
        <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1">
          {DATE_OPTS.map(o => (
            <button key={o.days} onClick={() => handleDaysChange(o.days)} data-testid={`date-range-${o.label}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${selectedDays === o.days ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
              {o.label}
            </button>
          ))}
        </div>
      </div>
      {/* GSC + GA4 block */}
      {googleData?.configured === false ? (
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-blue-100 p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
              <Globe className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-slate-900 text-sm mb-1" style={{fontFamily:'Outfit,sans-serif'}}>Connect Google Search Console + GA4</h3>
              <p className="text-xs text-slate-600 mb-3 leading-relaxed">
                Get real click/impression data, top search queries, rankings, and GA4 traffic metrics in this panel.
              </p>
              <div className="space-y-1.5 text-xs text-slate-600">
                <p className="font-semibold text-slate-700">Setup in 3 steps:</p>
                <p>1. Create a <strong>Service Account</strong> in Google Cloud Console → download JSON key</p>
                <p>2. Add the service account email to GSC (Full) and GA4 (Viewer) permissions</p>
                <p>3. Add these to <code className="bg-white px-1 rounded border border-slate-200">/app/backend/.env</code>:</p>
                <div className="bg-white rounded-lg border border-slate-200 p-2.5 font-mono text-xs mt-1 space-y-0.5">
                  <p>GOOGLE_SERVICE_ACCOUNT_JSON={"{"}{'"'}type{'"'}:{'"'}service_account{'"'},...{"}"}</p>
                  <p>GSC_SITE_URL=sc-domain:yourdomain.com</p>
                  <p>GA4_PROPERTY_ID=381234567</p>
                </div>
                <p className="text-slate-400 mt-1">Then run: <code className="bg-white px-1 rounded border border-slate-200">sudo supervisorctl restart backend</code></p>
              </div>
            </div>
          </div>
        </div>
      ) : googleData?.configured === true && (
        <div className="space-y-5">
          {/* GA4 totals with comparison deltas */}
          {googleData.ga4_totals && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <StatCard icon={Eye} label={`Sessions (${selectedDays}d)`} value={googleData.ga4_totals.sessions}
                color="bg-blue-50 text-blue-700"
                delta={calcDelta(googleData.ga4_totals.sessions, googleData.ga4_prev?.sessions)} />
              <StatCard icon={Users} label={`Users (${selectedDays}d)`} value={googleData.ga4_totals.users}
                color="bg-indigo-50 text-indigo-700"
                delta={calcDelta(googleData.ga4_totals.users, googleData.ga4_prev?.users)} />
              <StatCard icon={MousePointerClick} label={`Page Views (${selectedDays}d)`} value={googleData.ga4_totals.pageviews}
                color="bg-purple-50 text-purple-700"
                delta={calcDelta(googleData.ga4_totals.pageviews, googleData.ga4_prev?.pageviews)} />
              <StatCard icon={TrendingUp} label="Bounce Rate"
                value={`${googleData.ga4_totals.bounce_rate}%`} color="bg-rose-50 text-rose-700" />
            </div>
          )}

          {/* GSC top queries */}
          {googleData.gsc_queries?.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-100">
                <h3 className="text-sm font-bold text-slate-900" style={{fontFamily:'Outfit,sans-serif'}}>Top Search Queries (GSC)</h3>
                <p className="text-xs text-slate-400">Last {selectedDays} days — {googleData.period?.start} to {googleData.period?.end}</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="bg-slate-50 border-b border-slate-100">
                    <th className="text-left px-5 py-2.5 text-slate-500 font-semibold">Query</th>
                    <th className="text-right px-3 py-2.5 text-slate-500 font-semibold">Clicks</th>
                    <th className="text-right px-3 py-2.5 text-slate-500 font-semibold">Impr.</th>
                    <th className="text-right px-3 py-2.5 text-slate-500 font-semibold">CTR</th>
                    <th className="text-right px-5 py-2.5 text-slate-500 font-semibold">Position</th>
                  </tr></thead>
                  <tbody className="divide-y divide-slate-50">
                    {googleData.gsc_queries.map((q, i) => (
                      <tr key={i} className="hover:bg-slate-50 transition-colors">
                        <td className="px-5 py-2.5 text-slate-800 font-medium max-w-xs truncate">{q.query}</td>
                        <td className="px-3 py-2.5 text-right text-blue-700 font-semibold">{q.clicks}</td>
                        <td className="px-3 py-2.5 text-right text-slate-600">{q.impressions}</td>
                        <td className="px-3 py-2.5 text-right text-slate-600">{q.ctr}%</td>
                        <td className="px-5 py-2.5 text-right">
                          <span className={`font-bold ${q.position <= 10 ? 'text-emerald-600' : q.position <= 20 ? 'text-amber-600' : 'text-slate-400'}`}>
                            #{q.position}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* GA4 top pages */}
          {googleData.ga4_top_pages?.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-100">
                <h3 className="text-sm font-bold text-slate-900" style={{fontFamily:'Outfit,sans-serif'}}>Top Pages (GA4)</h3>
              </div>
              <div className="divide-y divide-slate-50">
                {googleData.ga4_top_pages.map((p, i) => (
                  <div key={i} className="px-5 py-2.5 flex items-center gap-3">
                    <span className="text-xs text-slate-400 w-4">{i + 1}</span>
                    <span className="flex-1 text-xs text-slate-700 font-medium truncate">{p.page}</span>
                    <span className="text-xs text-blue-700 font-semibold">{p.pageviews} views</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(googleData.gsc_error || googleData.ga4_error) && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-2">
              <div className="flex items-center gap-2 mb-1">
                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                <p className="text-sm font-bold text-amber-800">Action Required</p>
              </div>
              {googleData.gsc_error && (
                <p className="text-xs text-amber-700 leading-relaxed">{googleData.gsc_error}</p>
              )}
              {googleData.ga4_error && (
                <p className="text-xs text-amber-700 leading-relaxed">{googleData.ga4_error}</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Internal analytics divider */}
      <div className="flex items-center gap-3">
        <div className="flex-1 h-px bg-slate-200" />
        <span className="text-xs text-slate-400 font-medium">Internal Analytics (MongoDB)</span>
        <div className="flex-1 h-px bg-slate-200" />
      </div>

      {/* Stat cards with comparison deltas */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard icon={Eye} label={`Page Views (${selectedDays}d)`} value={data.totals.page_views_30d}
          color="bg-blue-50 text-blue-700"
          delta={calcDelta(data.totals.page_views_30d, data.totals.page_views_prev)} />
        <StatCard icon={Home} label={`Property Views (${selectedDays}d)`} value={data.totals.property_views_30d}
          color="bg-emerald-50 text-emerald-700"
          delta={calcDelta(data.totals.property_views_30d, data.totals.property_views_prev)} />
        <StatCard icon={MousePointerClick} label={`Contact Clicks (${selectedDays}d)`} value={data.totals.contact_clicks_30d}
          color="bg-amber-50 text-amber-700"
          delta={calcDelta(data.totals.contact_clicks_30d, data.totals.contact_clicks_prev)} />
        <StatCard icon={Search} label={`Searches (${selectedDays}d)`} value={data.totals.searches_30d}
          color="bg-purple-50 text-purple-700"
          delta={calcDelta(data.totals.searches_30d, data.totals.searches_prev)} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Daily page views chart */}
        {data.daily_page_views?.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
            <h3 className="text-sm font-bold text-slate-900 mb-4" style={{fontFamily:'Outfit,sans-serif'}}>Daily Page Views</h3>
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={data.daily_page_views} margin={{top:0,right:0,left:-20,bottom:0}}>
                <defs>
                  <linearGradient id="pvGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1d4ed8" stopOpacity={0.15}/>
                    <stop offset="95%" stopColor="#1d4ed8" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{fontSize:9,fill:'#94a3b8'}} tickFormatter={v => v?.slice(5)} />
                <YAxis tick={{fontSize:9,fill:'#94a3b8'}} />
                <Tooltip contentStyle={{borderRadius:'12px',border:'1px solid #e2e8f0',fontSize:'12px'}} />
                <Area type="monotone" dataKey="views" stroke="#1d4ed8" strokeWidth={2} fill="url(#pvGrad)" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Top cities */}
        {data.top_cities?.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
            <h3 className="text-sm font-bold text-slate-900 mb-4" style={{fontFamily:'Outfit,sans-serif'}}>Top Cities (Property Views)</h3>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={data.top_cities} layout="vertical" margin={{top:0,right:16,left:0,bottom:0}}>
                <XAxis type="number" tick={{fontSize:9,fill:'#94a3b8'}} />
                <YAxis dataKey="city" type="category" tick={{fontSize:10,fill:'#475569'}} width={72} />
                <Tooltip contentStyle={{borderRadius:'12px',border:'1px solid #e2e8f0',fontSize:'12px'}} />
                <Bar dataKey="views" radius={[0,6,6,0]}>
                  {data.top_cities.map((_, i) => <Cell key={i} fill={CITY_COLORS[i % CITY_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Event breakdown */}
        {data.event_breakdown?.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
            <h3 className="text-sm font-bold text-slate-900 mb-4" style={{fontFamily:'Outfit,sans-serif'}}>User Actions (30 days)</h3>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={data.event_breakdown} margin={{top:0,right:0,left:-20,bottom:0}}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="event" tick={{fontSize:8,fill:'#94a3b8'}} />
                <YAxis tick={{fontSize:9,fill:'#94a3b8'}} />
                <Tooltip contentStyle={{borderRadius:'12px',border:'1px solid #e2e8f0',fontSize:'12px'}} />
                <Bar dataKey="count" radius={[6,6,0,0]}>
                  {data.event_breakdown.map((_, i) => <Cell key={i} fill={CITY_COLORS[i % CITY_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Tenant demand by city */}
        {data.requirements_by_city?.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
            <h3 className="text-sm font-bold text-slate-900 mb-4" style={{fontFamily:'Outfit,sans-serif'}}>Tenant Demand by City</h3>
            <div className="space-y-2">
              {data.requirements_by_city.map((r, i) => {
                const max = data.requirements_by_city[0].count;
                const pct = Math.round((r.count / max) * 100);
                return (
                  <div key={i} className="flex items-center gap-3">
                    <p className="text-xs text-slate-600 w-20 truncate">{r.city}</p>
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{width:`${pct}%`, background: CITY_COLORS[i % CITY_COLORS.length]}} />
                    </div>
                    <p className="text-xs font-semibold text-slate-700 w-6 text-right">{r.count}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Market Intelligence ───────────────────────────────────────────────────────

const DEMAND_STYLES = {
  'EXTREME':   { cls: 'bg-red-100 text-red-700 border-red-300',    icon: Flame,     bar: 'bg-red-500' },
  'VERY HIGH': { cls: 'bg-orange-100 text-orange-700 border-orange-300', icon: TrendingUp, bar: 'bg-orange-400' },
  'HIGH':      { cls: 'bg-amber-100 text-amber-700 border-amber-300',    icon: TrendingUp, bar: 'bg-amber-400' },
};
const RANK_COLORS = ['#dc2626','#ea580c','#d97706','#ca8a04','#65a30d','#16a34a','#0891b2','#2563eb','#7c3aed','#9333ea'];

function MarketIntelligence({ onOpenWriter }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const loadReport = async (refresh = false) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/growth/market-intelligence${refresh ? '?refresh=true' : ''}`,
        { credentials: 'same-origin' });
      if (!res.ok) throw new Error('Failed to generate report');
      setData(await res.json());
      if (refresh) toast.success('Market report updated!');
    } catch (e) { toast.error(e.message || 'Failed to load report'); }
    setLoading(false);
  };

  useEffect(() => { loadReport(); }, []);

  const localities = data?.ai_data?.top_localities || [];
  const genDate = data?.generated_at
    ? new Date(data.generated_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;

  return (
    <div className="space-y-5" data-testid="market-intelligence">
      {/* Header card */}
      <div className="bg-gradient-to-br from-indigo-950 via-indigo-900 to-purple-900 rounded-2xl p-5 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage:'radial-gradient(circle at 70% 50%, #a78bfa 0%, transparent 60%)'}} />
        <div className="relative flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-7 h-7 bg-amber-400/20 rounded-lg flex items-center justify-center">
                <Award className="w-4 h-4 text-amber-400" />
              </div>
              <span className="text-xs font-bold uppercase tracking-widest text-indigo-300">AI Market Intelligence</span>
            </div>
            <h2 className="text-xl font-black text-white mb-1" style={{fontFamily:'Outfit,sans-serif'}}>
              Top 10 Rental Demand Localities
            </h2>
            <p className="text-indigo-300 text-sm">India-wide · Updated monthly · AI + Platform Analytics</p>
            {genDate && <p className="text-indigo-400 text-xs mt-1">Last generated: {genDate}</p>}
          </div>
          <button onClick={() => loadReport(true)} disabled={loading} data-testid="generate-market-report"
            className="flex items-center gap-2 px-4 py-2.5 bg-white/10 hover:bg-white/20 border border-white/20 text-white rounded-xl text-sm font-bold transition-all disabled:opacity-60 flex-shrink-0">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {loading ? 'Analyzing India…' : 'Generate Report'}
          </button>
        </div>
      </div>

      {/* Loading skeleton */}
      {loading && !data && (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="bg-white rounded-2xl border border-slate-100 p-4 animate-pulse">
              <div className="flex gap-3">
                <div className="w-8 h-8 bg-slate-200 rounded-xl" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-slate-200 rounded w-1/3" />
                  <div className="h-3 bg-slate-100 rounded w-2/3" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* AI Market Insight */}
      {data?.ai_data?.market_insight && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-2">
            <Lightbulb className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-bold text-amber-900" style={{fontFamily:'Outfit,sans-serif'}}>Market Insight</span>
          </div>
          <p className="text-sm text-amber-900 leading-relaxed">{data.ai_data.market_insight}</p>
        </div>
      )}

      {/* Top 10 Locality Cards */}
      {localities.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-bold text-slate-700 px-1" style={{fontFamily:'Outfit,sans-serif'}}>
            Top Localities to Target
          </h3>
          {localities.map((loc, i) => {
            const style = DEMAND_STYLES[loc.demand_level] || DEMAND_STYLES['HIGH'];
            const DIcon = style.icon;
            const isOpen = expanded === i;
            const fullName = loc.locality ? `${loc.locality}, ${loc.city}` : loc.city;
            return (
              <div key={i} data-testid={`locality-card-${i}`}
                className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                {/* Rank accent bar */}
                <div className="h-0.5 w-full" style={{backgroundColor: RANK_COLORS[i] || '#64748b'}} />
                <div className="p-4">
                  <div className="flex items-start gap-3">
                    {/* Rank badge */}
                    <div className="flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center text-sm font-black text-white"
                      style={{backgroundColor: RANK_COLORS[i] || '#64748b'}}>
                      {loc.rank || i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="text-sm font-bold text-slate-900" style={{fontFamily:'Outfit,sans-serif'}}>
                          {fullName}
                        </h4>
                        <span className={`text-xs px-2 py-0.5 rounded-full border font-bold flex items-center gap-1 ${style.cls}`}>
                          <DIcon className="w-3 h-3" />
                          {loc.demand_level}
                        </span>
                        {loc.monthly_searches_est && (
                          <span className="text-xs text-slate-400">
                            ~{(loc.monthly_searches_est / 1000).toFixed(1)}K/mo searches
                          </span>
                        )}
                      </div>
                      {/* Drivers */}
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {(loc.drivers || []).map((d, di) => (
                          <span key={di} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{d}</span>
                        ))}
                      </div>
                      {/* Expanded details */}
                      {isOpen && (
                        <div className="mt-3 pt-3 border-t border-slate-100 space-y-1.5">
                          <div className="flex gap-2 text-xs">
                            <span className="text-slate-400 w-28 flex-shrink-0">Best property types</span>
                            <span className="text-slate-700 font-medium">{loc.best_property_types}</span>
                          </div>
                          {loc.target_keyword && (
                            <div className="flex gap-2 text-xs">
                              <span className="text-slate-400 w-28 flex-shrink-0">Target keyword</span>
                              <span className="text-blue-700 font-mono bg-blue-50 px-2 py-0.5 rounded">{loc.target_keyword}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {/* Actions */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={() => setExpanded(isOpen ? null : i)}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors">
                        <ChevronRight className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                      </button>
                      <button data-testid={`write-blog-${i}`}
                        onClick={() => onOpenWriter(
                          loc.target_keyword || `rental properties in ${fullName}`,
                          `${loc.demand_level} demand: ${(loc.drivers || []).join(', ')}`
                        )}
                        className="flex items-center gap-1 px-2.5 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold transition-colors whitespace-nowrap">
                        <BookOpen className="w-3 h-3" />
                        Write Blog
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Internal signals (when AI data not available) */}
      {!localities.length && data?.top_cities_internal?.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100">
            <h3 className="text-sm font-bold text-slate-900" style={{fontFamily:'Outfit,sans-serif'}}>Internal Demand Signals</h3>
            <p className="text-xs text-slate-400">Based on platform activity — click Generate Report for AI analysis</p>
          </div>
          <div className="divide-y divide-slate-50">
            {data.top_cities_internal.slice(0, 5).map((c, i) => (
              <div key={i} className="px-5 py-3 flex items-center gap-3">
                <span className="text-xs font-bold text-slate-400 w-4">{i + 1}</span>
                <span className="flex-1 text-sm font-semibold text-slate-900">{c.city}</span>
                <span className="text-xs text-slate-500">{c.views} views</span>
                <span className="text-xs text-slate-500">{c.requirements} req.</span>
                <span className="text-xs font-bold text-indigo-700">{c.demand_score} pts</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Trending + Opportunities */}
      {(data?.ai_data?.trending_up?.length > 0 || data?.ai_data?.opportunity_gaps?.length > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {data.ai_data.trending_up?.length > 0 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-4 h-4 text-emerald-600" />
                <span className="text-sm font-bold text-emerald-900" style={{fontFamily:'Outfit,sans-serif'}}>Trending Up</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {data.ai_data.trending_up.map((c, i) => (
                  <button key={i} onClick={() => onOpenWriter(`rental properties in ${c}`, c)}
                    className="text-xs bg-emerald-100 hover:bg-emerald-200 text-emerald-800 px-2.5 py-1 rounded-full font-medium transition-colors">
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}
          {data?.ai_data?.opportunity_gaps?.length > 0 && (
            <div className="bg-purple-50 border border-purple-200 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Target className="w-4 h-4 text-purple-600" />
                <span className="text-sm font-bold text-purple-900" style={{fontFamily:'Outfit,sans-serif'}}>Opportunity Gaps</span>
                <span className="text-xs text-purple-500">(high demand, low supply)</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {data.ai_data.opportunity_gaps.map((c, i) => (
                  <button key={i} onClick={() => onOpenWriter(`rental properties in ${c}`, c)}
                    className="text-xs bg-purple-100 hover:bg-purple-200 text-purple-800 px-2.5 py-1 rounded-full font-medium transition-colors">
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {!loading && !data && (
        <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-10 text-center">
          <MapPin className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm font-bold text-slate-700 mb-1">No report yet</p>
          <p className="text-xs text-slate-400 mb-4">Generate your first AI market intelligence report to identify top rental demand localities across India.</p>
          <button onClick={() => loadReport(true)} disabled={loading}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-bold transition-colors">
            Generate Now
          </button>
        </div>
      )}

      {/* How to use */}
      <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 text-xs text-slate-500 space-y-1">
        <p><strong className="text-slate-700">How to use this:</strong></p>
        <p>1. Click <strong>Write Blog</strong> next to any locality to auto-generate a targeted SEO blog post</p>
        <p>2. Click <strong>Trending Up</strong> / <strong>Opportunity Gaps</strong> cities to open the blog writer</p>
        <p>3. Enable <strong>Market Intelligence</strong> in the Automation tab to auto-refresh monthly</p>
      </div>
    </div>
  );
}


// ── Main GrowthHub ─────────────────────────────────────────────────────────────

// ── Auto-Published Blogs List ─────────────────────────────────────────────────

function AutoPublishedBlogs({ refreshTrigger }) {
  const [blogs, setBlogs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState({});

  const fetchBlogs = useCallback(async () => {
    try {
      const res = await fetch(`${API}/growth/auto-blog/published`, { credentials: 'same-origin' });
      const d = await res.json();
      setBlogs(d.blogs || []);
    } catch { setBlogs([]); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchBlogs(); }, [fetchBlogs, refreshTrigger]);

  const toggleStatus = async (blogId, currentStatus) => {
    const newStatus = currentStatus === 'published' ? 'draft' : 'published';
    setToggling(p => ({ ...p, [blogId]: true }));
    try {
      await fetch(`${API}/growth/auto-blog/published/${blogId}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ status: newStatus }),
      });
      fetchBlogs();
      toast.success(`Blog ${newStatus}`);
    } catch { toast.error('Failed to update blog'); }
    setToggling(p => ({ ...p, [blogId]: false }));
  };

  if (loading) return <div className="flex justify-center py-6"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></div>;
  if (!blogs?.length) return (
    <p className="text-xs text-slate-400 text-center py-6">No auto-published blogs yet. Run the Auto-Blog Publisher to create one.</p>
  );

  return (
    <div className="divide-y divide-slate-50">
      {blogs.map(b => (
        <div key={b.blog_id} className="px-5 py-3 flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">{b.title}</p>
            <p className="text-xs text-slate-400 mt-0.5">
              {b.published_at ? new Date(b.published_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Draft'}
              {b.views ? ` · ${b.views} views` : ''}
            </p>
          </div>
          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold border ${b.status === 'published' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
            {b.status === 'published' ? 'Live' : 'Draft'}
          </span>
          {b.slug && (
            <a href={`/blog/${b.slug}`} target="_blank" rel="noreferrer"
              className="p-1.5 rounded-lg text-slate-300 hover:text-blue-600 hover:bg-blue-50 transition-colors">
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
          <button onClick={() => toggleStatus(b.blog_id, b.status)} disabled={toggling[b.blog_id]}
            data-testid={`toggle-blog-${b.blog_id}`}
            className="p-1.5 rounded-lg text-slate-300 hover:text-amber-600 hover:bg-amber-50 transition-colors disabled:opacity-40">
            {toggling[b.blog_id] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> :
              b.status === 'published' ? <ToggleRight className="w-3.5 h-3.5 text-emerald-500" /> : <ToggleLeft className="w-3.5 h-3.5" />}
          </button>
        </div>
      ))}
    </div>
  );
}

// ── Automation Center ─────────────────────────────────────────────────────────

const FREQ_OPTIONS = [
  { label: 'Daily', days: 1 },
  { label: 'Weekly', days: 7 },
  { label: 'Bi-weekly', days: 14 },
  { label: 'Monthly', days: 30 },
];

function AutomationCenter() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState({});
  const [blogRefresh, setBlogRefresh] = useState(0);

  const fetchStatus = async () => {
    try {
      const res = await fetch(`${API}/growth/automation`, { credentials: 'same-origin' });
      setStatus(await res.json());
    } catch { toast.error('Failed to load automation status'); }
    setLoading(false);
  };

  useEffect(() => { fetchStatus(); }, []);

  const toggle = async (type, enabled) => {
    setStatus(prev => ({ ...prev, [type]: { ...prev[type], enabled } }));
    await fetch(`${API}/growth/automation`, {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type, enabled }),
    });
    toast.success(`${enabled ? 'Enabled' : 'Disabled'} ${type === 'auto_blog' ? 'Auto-Blog' : 'Keyword Gap'} automation`);
  };

  const updateFrequency = async (type, freqDays) => {
    setStatus(prev => ({ ...prev, [type]: { ...prev[type], frequency_days: freqDays } }));
    await fetch(`${API}/growth/automation`, {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type, frequency_days: freqDays }),
    });
    toast.success('Frequency updated');
  };

  const runNow = async (type) => {
    setRunning(p => ({ ...p, [type]: true }));
    try {
      const res = await fetch(`${API}/growth/automation`, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, run_now: true }),
      });
      const d = await res.json();
      toast.success(d.message || 'Job started in background');
      setTimeout(() => {
        fetchStatus();
        setRunning(p => ({ ...p, [type]: false }));
        if (type === 'auto_blog') setBlogRefresh(n => n + 1);
      }, 5000);
    } catch { setRunning(p => ({ ...p, [type]: false })); toast.error('Failed to trigger job'); }
  };  const fmt = (iso) => {
    if (!iso) return 'Never';
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) + ' ' +
      d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  const nextRun = (lastRun, freqDays = 7) => {
    if (!lastRun) return 'On first enable';
    const next = new Date(new Date(lastRun).getTime() + freqDays * 24 * 60 * 60 * 1000);
    const diff = Math.round((next - Date.now()) / (1000 * 60 * 60 * 24));
    if (diff <= 0) return 'Due now';
    return `In ${diff} day${diff !== 1 ? 's' : ''}`;
  };

  const ResultBadge = ({ result }) => {
    if (!result) return null;
    const ok = result.status === 'success';
    const skip = result.status === 'skipped';
    let label;
    if (ok) {
      if (result.blog_title) label = `Published: "${result.blog_title.slice(0,35)}…"`;
      else if (result.localities !== undefined) label = `${result.localities} localities analysed`;
      else label = `${result.quick_wins || 0} quick wins found`;
    } else if (skip) {
      label = result.reason?.slice(0, 50);
    } else {
      label = `Error: ${result.reason?.slice(0, 50)}`;
    }
    return (
      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${ok ? 'bg-emerald-100 text-emerald-700' : skip ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
        {label}
      </span>
    );
  };

  const JOB_CARDS = [
    {
      type: 'auto_blog',
      icon: BookOpen,
      color: 'purple',
      title: 'Auto-Blog Publisher',
      description: 'Scans property clusters. Finds cities with 5+ listings but no blog, writes and publishes a full SEO guide automatically.',
      what: 'Generates 800-1000 word ranking blog post',
    },
    {
      type: 'keyword_gaps',
      icon: Zap,
      color: 'emerald',
      title: 'Keyword Gap Monitor',
      description: 'Pulls your Google Search Console data. Tracks queries ranking 6–50 and shows which ones need a blog to reach page 1.',
      what: 'Caches GSC data for instant Gap Alerts view',
    },
    {
      type: 'market_intelligence',
      icon: MapPin,
      color: 'indigo',
      title: 'Market Intelligence',
      description: 'Aggregates platform demand signals (views, searches, requirements) + AI analysis to update the Top 10 rental demand localities monthly.',
      what: 'Updates Market Intel tab with fresh AI report',
    },
  ];

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
    </div>
  );

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-5 text-white">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center">
            <Zap className="w-4 h-4 text-emerald-400" />
          </div>
          <h3 className="font-black text-lg" style={{fontFamily:'Outfit,sans-serif'}}>SEO Automation</h3>
        </div>
        <p className="text-slate-300 text-sm">Set once, AI publishes blogs and monitors rankings automatically on your chosen schedule.</p>
      </div>

      {/* Job Cards */}
      {JOB_CARDS.map(({ type, icon: Icon, color, title, description, what }) => {
        const s = status?.[type] || {};
        const isEnabled = s.enabled || false;
        const isRunning = running[type];
        const freqDays = s.frequency_days || 7;
        const colorMap = {
          purple: { bg: 'bg-purple-50', border: 'border-purple-200', icon: 'bg-purple-100 text-purple-700', btn: 'bg-purple-700 hover:bg-purple-800', toggle: 'bg-purple-600' },
          emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', icon: 'bg-emerald-100 text-emerald-700', btn: 'bg-emerald-700 hover:bg-emerald-800', toggle: 'bg-emerald-600' },
          indigo: { bg: 'bg-indigo-50', border: 'border-indigo-200', icon: 'bg-indigo-100 text-indigo-700', btn: 'bg-indigo-700 hover:bg-indigo-800', toggle: 'bg-indigo-600' },
        };
        const c = colorMap[color];

        return (
          <div key={type} className={`rounded-2xl border ${isEnabled ? c.border : 'border-slate-100'} bg-white shadow-sm overflow-hidden`}>
            <div className="p-5">
              <div className="flex items-start gap-3">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${c.icon}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <h4 className="font-bold text-slate-900 text-sm" style={{fontFamily:'Outfit,sans-serif'}}>{title}</h4>
                    <button onClick={() => toggle(type, !isEnabled)} data-testid={`toggle-${type}`}
                      className={`relative inline-flex w-10 h-5 rounded-full transition-colors flex-shrink-0 ${isEnabled ? c.toggle : 'bg-slate-200'}`}>
                      <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${isEnabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </button>
                    <span className={`text-xs font-semibold ${isEnabled ? 'text-emerald-600' : 'text-slate-400'}`}>
                      {isEnabled ? 'ON' : 'OFF'}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 leading-relaxed">{description}</p>
                </div>
              </div>

              {/* Stats row */}
              <div className="mt-4 grid grid-cols-3 gap-3">
                <div className="bg-slate-50 rounded-xl p-3">
                  <p className="text-xs text-slate-400 mb-0.5">Last Run</p>
                  <p className="text-xs font-semibold text-slate-700">{fmt(s.last_run)}</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-3">
                  <p className="text-xs text-slate-400 mb-0.5">Next Run</p>
                  <p className="text-xs font-semibold text-slate-700">{isEnabled ? nextRun(s.last_run, freqDays) : '—'}</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-3">
                  <p className="text-xs text-slate-400 mb-1">Frequency</p>
                  <select value={freqDays} onChange={e => updateFrequency(type, parseInt(e.target.value))}
                    data-testid={`freq-select-${type}`}
                    className="text-xs font-semibold text-slate-700 bg-transparent outline-none w-full cursor-pointer">
                    {FREQ_OPTIONS.map(o => (
                      <option key={o.days} value={o.days}>{o.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Last result */}
              {s.last_result && (
                <div className="mt-3">
                  <p className="text-xs text-slate-400 mb-1">Last Result</p>
                  <ResultBadge result={s.last_result} />
                </div>
              )}

              {/* Actions */}
              <div className="mt-4 flex items-center gap-2">
                <button onClick={() => runNow(type)} disabled={isRunning}
                  data-testid={`run-now-${type}`}
                  className={`flex items-center gap-1.5 px-3 py-2 text-white rounded-xl text-xs font-bold disabled:opacity-60 transition-colors ${c.btn}`}>
                  {isRunning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />}
                  {isRunning ? 'Running…' : 'Run Now'}
                </button>
                <button onClick={fetchStatus}
                  className="flex items-center gap-1.5 px-3 py-2 border border-slate-200 text-slate-600 rounded-xl text-xs font-semibold hover:bg-slate-50 transition-colors">
                  <RefreshCw className="w-3.5 h-3.5" /> Refresh
                </button>
                <span className="text-xs text-slate-400 ml-auto hidden sm:block">{what}</span>
              </div>
            </div>
          </div>
        );
      })}

      {/* Auto-Published Blogs */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BookOpen className="w-3.5 h-3.5 text-purple-600" />
            <h4 className="font-bold text-slate-900 text-sm" style={{fontFamily:'Outfit,sans-serif'}}>AI-Published Blogs</h4>
          </div>
          <span className="text-xs text-slate-400">Toggle to publish / unpublish</span>
        </div>
        <AutoPublishedBlogs refreshTrigger={blogRefresh} />
      </div>

      {/* Info */}
      <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 text-xs text-blue-700">
        <strong>How it works:</strong> Toggle ON to activate a job. Use <strong>Frequency</strong> to set how often it runs. Use <strong>Run Now</strong> to trigger immediately. Results appear in the Last Result badge within ~30 seconds.
      </div>
    </div>
  );
}


const GROWTH_TABS = [
  { id: 'blog-writer', label: 'AI Blog Writer', icon: Sparkles },
  { id: 'keywords', label: 'Keywords', icon: Tag },
  { id: 'market', label: 'Market Intel', icon: MapPin },
  { id: 'seo-checker', label: 'SEO Checker', icon: Globe },
  { id: 'calendar', label: 'Content Calendar', icon: Calendar },
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  { id: 'automation', label: 'Automation', icon: Zap },
];

export default function GrowthHub({ blogs, onRefreshBlogs }) {
  const [activeTab, setActiveTab] = useState('blog-writer');
  const [prefill, setPrefill] = useState(null);

  const openWriter = useCallback((topic, keyword) => {
    setPrefill({ topic, keyword });
    setActiveTab('blog-writer');
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-5">
      {/* Sidebar */}
      <div className="lg:w-48 flex-shrink-0">
        <div className="bg-white rounded-2xl border border-slate-100 p-2 shadow-sm">
          <div className="flex items-center gap-2 px-3 py-2 mb-1">
            <div className="w-6 h-6 bg-emerald-600 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="text-xs font-black text-slate-900 uppercase tracking-wider" style={{fontFamily:'Outfit,sans-serif'}}>Growth Hub</span>
          </div>
          {GROWTH_TABS.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-all text-left ${
                activeTab === t.id
                  ? 'bg-emerald-600 text-white shadow-sm'
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
              }`}
              data-testid={`growth-tab-${t.id}`}>
              <t.icon className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate">{t.label}</span>
              {activeTab === t.id && <ChevronRight className="w-3 h-3 ml-auto flex-shrink-0" />}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {activeTab === 'blog-writer' && (
          <AiBlogWriter blogs={blogs} onRefreshBlogs={onRefreshBlogs}
            prefill={prefill} onClearPrefill={() => setPrefill(null)} />
        )}
        {activeTab === 'keywords' && <KeywordPlanner onOpenWriter={openWriter} />}
        {activeTab === 'market' && <MarketIntelligence onOpenWriter={openWriter} />}
        {activeTab === 'seo-checker' && <SeoChecker />}
        {activeTab === 'calendar' && <ContentCalendar blogs={blogs} />}
        {activeTab === 'analytics' && <GrowthAnalytics />}
        {activeTab === 'automation' && <AutomationCenter />}
      </div>
    </div>
  );
}
